/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/bolmateProductRow.scss';

import {Icon} from '@iconify/react';

import classNames from 'classnames';

import React, {ReactNode, useLayoutEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import BolmateBadge, {BolmateBadgeColor} from '../badge/bolmateBadge';

import BolmateExternalLink from '../externalLink/bolmateExternalLink';

import BolmateImageFallback, {ImageBadgeColor} from '../imageFallback/bolmateImageFallback';
import {OverviewReturn} from '../../../util/interfaces.ts';

export type ProductRowTone = 'default' | 'positive' | 'negative' | 'muted';

export interface ProductRowStat {
    label?: ReactNode;
    value: ReactNode;
    tone?: ProductRowTone;
    className?: string;
}

export interface ProductRowBadge {
    label: ReactNode;
    color?: BolmateBadgeColor;
    icon?: string;
    /** Extra text lines under the label, turning the pill into an expandable box. */
    details?: Array<string>;
    className?: string;
}

export interface ProductRowReview {
    rating: number;
    title?: string | null;
    message?: string | null;
    author?: string | null;
}

interface Props {
    imageUrl: string | null;
    /** Overlay pinned to the thumbnail (quantity pill, won/lost marker). */
    imageBadge?: ReactNode;
    imageBadgeTitle?: string;
    imageBadgeColor?: ImageBadgeColor;
    title: string;
    /** Renders the title as an external link (bol.com product page) when set. */
    titleHref?: string | null;
    ean: string;
    /** Secondary line under the EAN — a date in most sections. */
    subtitle?: ReactNode;
    badge?: ProductRowBadge;
    review?: ProductRowReview;
    returnItem?: OverviewReturn;
    primaryStat?: ProductRowStat;
    secondaryStat?: ProductRowStat;
    /** Muted line for context that is not a metric (customer, competitor, RMA). */
    footnote?: ReactNode;
    className?: string;
}

const MAX_RATING = 5;

function useClamped(
    ref: React.RefObject<HTMLElement | null>,
    expanded: boolean,
    content: string | null | undefined,
    selector?: string
): boolean {
    const [clamped, setClamped] = useState(false);

    useLayoutEffect(() => {
        function measure() {
            const node = ref.current;
            if (!node || expanded) return;
            const targets = selector ? Array.from(node.querySelectorAll(selector)) : [node];
            setClamped(targets.some(target => target.scrollHeight - target.clientHeight > 1));
        }

        measure();
        window.addEventListener('resize', measure);
        return function () {
            window.removeEventListener('resize', measure);
        };
    }, [ref, selector, content, expanded]);

    return clamped;
}

/**
 * One product line of the overview sections: thumbnail, title + EAN, an
 * optional status badge or review quote, and up to two metrics. Desktop splits
 * these into three columns; below the mobile breakpoint they stack.
 */
export default function BolmateProductRow(props: Props) {
    const {
        imageUrl,
        imageBadge,
        imageBadgeTitle,
        imageBadgeColor,
        title,
        titleHref,
        ean,
        subtitle,
        badge,
        review,
        returnItem,
        primaryStat,
        secondaryStat,
        footnote,
        className
    } = props;
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [badgeExpanded, setBadgeExpanded] = useState(false);
    const messageRef = useRef<HTMLSpanElement>(null);
    const badgeBoxRef = useRef<HTMLDivElement>(null);
    const badgeDetails = badge?.details?.filter(detail => !!detail) ?? [];
    const reviewClamped = useClamped(messageRef, expanded, review?.message);
    const badgeClamped = useClamped(badgeBoxRef, badgeExpanded, badgeDetails.join('\n'), '.product-row-badge-detail');
    const hasCenter = !!badge || !!review || !!returnItem;
    const hasMetrics = !!primaryStat || !!secondaryStat || !!footnote;

    function onToggleReview() {
        setExpanded(current => !current);
    }

    function onToggleBadgeBox() {
        if (!badgeClamped && !badgeExpanded) return;
        setBadgeExpanded(current => !current);
    }

    function onBadgeBoxKeyDown(event: React.KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onToggleBadgeBox();
    }

    function statClass(stat: ProductRowStat | undefined): string | undefined {
        return stat?.tone && stat.tone !== 'default' ? stat.tone : undefined;
    }

    function renderThumbnail() {
        return (
            <BolmateImageFallback
                src={imageUrl}
                alt={title}
                className='product-row-thumb'
                imageClassName='product-row-image'
                badge={imageBadge}
                badgeTitle={imageBadgeTitle}
                badgeColor={imageBadgeColor}
            />
        );
    }

    function renderTitle() {
        if (titleHref)
            return (
                <BolmateExternalLink href={titleHref} className='product-row-title' title={title}>
                    <span className='product-row-title-text'>{title}</span>
                </BolmateExternalLink>
            );
        return (
            <span className='product-row-title' title={title}>
                {title}
            </span>
        );
    }

    function renderIdentity() {
        return (
            <div className='product-row-identity'>
                {renderThumbnail()}
                <div className='product-row-labels'>
                    {renderTitle()}
                    <span className='product-row-ean'>EAN: {ean}</span>
                    {!!subtitle && <span className='product-row-subtitle'>{subtitle}</span>}
                </div>
            </div>
        );
    }

    function renderBadge() {
        if (!badge) return null;
        if (badgeDetails.length) return renderBadgeBox();
        return (
            <BolmateBadge
                className={classNames('product-row-badge', badge.className)}
                label={
                    <>
                        {!!badge.icon && <Icon icon={badge.icon} className='product-row-badge-icon' />}
                        <span className='product-row-badge-label'>{badge.label}</span>
                    </>
                }
                color={badge.color}
                size='small'
                outline
            />
        );
    }

    function renderBadgeBox() {
        if (!badge) return null;
        const expandable = badgeClamped || badgeExpanded;
        return (
            <div
                ref={badgeBoxRef}
                className={classNames(
                    'product-row-badge-box',
                    badge.color ?? 'red',
                    expandable && 'expandable',
                    badgeExpanded && 'expanded',
                    badge.className
                )}
                onClick={onToggleBadgeBox}
                onKeyDown={expandable ? onBadgeBoxKeyDown : undefined}
                role={expandable ? 'button' : undefined}
                tabIndex={expandable ? 0 : undefined}
                aria-expanded={expandable ? badgeExpanded : undefined}
                title={expandable && !badgeExpanded ? t('controls.productRow.readMore', 'Meer lezen') : undefined}
            >
                <span className='product-row-badge-box-head'>
                    {!!badge.icon && <Icon icon={badge.icon} className='product-row-badge-icon' />}
                    <span className='product-row-badge-label'>{badge.label}</span>
                </span>
                {badgeDetails.map((detail, index) => (
                    <span key={index} className='product-row-badge-detail'>
                        {detail}
                    </span>
                ))}
            </div>
        );
    }

    function renderStars() {
        if (!review) return null;
        return (
            <div className='product-row-stars'>
                {Array.from({length: MAX_RATING}, (_value, index) => (
                    <Icon
                        key={index}
                        icon={index < Math.round(review.rating) ? 'mdi:star' : 'mdi:star-outline'}
                        className={classNames('product-row-star', index < Math.round(review.rating) && 'filled')}
                    />
                ))}
                <span className='product-row-rating'>{review.rating.toFixed(1).replace('.', ',')}</span>
            </div>
        );
    }

    function renderReviewMessage() {
        if (!review?.message) return null;
        return (
            <>
                <span ref={messageRef} className={classNames('product-row-review-message', expanded && 'expanded')}>
                    {review.message}
                </span>
                {(reviewClamped || expanded) && (
                    <button type='button' className='product-row-review-more' onClick={onToggleReview}>
                        {expanded
                            ? t('controls.productRow.readLess', 'Minder lezen')
                            : t('controls.productRow.readMore', 'Meer lezen')}
                    </button>
                )}
            </>
        );
    }

    function renderReview() {
        if (!review) return null;
        return (
            <div className='product-row-review'>
                <div className='product-row-review-head'>
                    {renderStars()}
                    {!!review.author && <span className='product-row-review-author'>{review.author}</span>}
                </div>
                {!!review.title && <span className='product-row-review-title'>“{review.title}”</span>}
                {renderReviewMessage()}
            </div>
        );
    }

    function renderReturnItem() {
        if (!returnItem) return null;
        const rma = `RMA: ${returnItem.rma_id}`;
        return (
            <div className={classNames('product-row-return', 'click')}>
                <div className='product-return-details'>
                    <div className='product-row-return-head'>
                        <Icon icon='mdi:package-variant-closed-remove' />
                        <span className='return-reason'>
                            {returnItem.return_reason ?? t('bol.returnReason.noReason', 'Geen reden')}
                        </span>
                    </div>
                    <span className='return-detail'>{returnItem.return_detail}</span>
                    <p className={classNames('return-comment', {expanded})} onClick={onToggleReview}>
                        {returnItem.comment}
                    </p>
                </div>
                <div className='return-meta'>
                    <span>{t('overview.returns.quantity', '{{count}} stuk retour', {count: returnItem.quantity})}</span>
                    <span>{returnItem.customer_name ? `${returnItem.customer_name} • ${rma}` : rma}</span>
                </div>
            </div>
        );
    }

    function renderStat(stat: ProductRowStat | undefined, variant: 'primary' | 'secondary') {
        if (!stat) return null;
        return (
            <div className={classNames(`product-row-stat`, variant, statClass(stat), stat.className)}>
                {!!stat.label && <span className='product-row-stat-label'>{stat.label}</span>}
                <span className='product-row-stat-value'>{stat.value}</span>
            </div>
        );
    }

    return (
        <div
            className={classNames(
                'bolmate-product-row',
                !hasCenter && 'no-center',
                !hasMetrics && 'no-metrics',
                className
            )}
        >
            {renderIdentity()}
            {hasCenter && (
                <div className='product-row-center'>
                    {renderBadge()}
                    {renderReview()}
                    {renderReturnItem()}
                </div>
            )}
            {hasMetrics && (
                <div className='product-row-metrics'>
                    {renderStat(primaryStat, 'primary')}
                    {renderStat(secondaryStat, 'secondary')}
                    {!!footnote && <span className='product-row-footnote'>{footnote}</span>}
                </div>
            )}
        </div>
    );
}
